<template>
  <div v-if="isAdmin">
    <div class="d-flex align-items-center justify-content-between mb-1">
      <h4 class="mb-0" style="color: #7f1d1a; font-weight: 600;">
        <feather-icon icon="MailIcon" class="mr-50" size="20" />
        Email Logs
      </h4>
      <b-button
        variant="outline-danger"
        size="sm"
        :disabled="selected.length === 0 || loadingBulkDelete"
        @click="confirmBulkDelete"
      >
        <feather-icon icon="TrashIcon" class="mr-25" size="14" />
        Delete selected ({{ selected.length }})
      </b-button>
    </div>
    
    <b-card no-body class="mb-0">
      <div class="d-flex align-items-center justify-content-between px-1 py-1 border-bottom flex-wrap" style="gap: 8px;">
        <div class="d-flex align-items-center" style="gap: 4px;">
          <b-button
            v-for="f in filters"
            :key="f.value"
            :variant="activeFilter === f.value ? 'primary' : 'flat-secondary'"
            size="sm"
            @click="setFilter(f.value)"
          >
            {{ f.label }}
            <b-badge
              :variant="f.value === 'success' ? 'light-success' : f.value === 'failed' ? 'light-danger' : 'light-secondary'"
              class="ml-25"
            >
              {{ counts[f.value] || 0 }}
            </b-badge>
          </b-button>
        </div>

        <div class="d-flex align-items-center" style="gap: 8px;">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search receiver or subject..."
            size="sm"
            style="width: 240px;"
            @input="onSearch"
          />
          <b-button variant="flat-secondary" size="sm" @click="loadLogs" :disabled="loading">
            <feather-icon :icon="loading ? 'LoaderIcon' : 'RefreshCwIcon'" size="14" :class="{ 'spin': loading }" />
          </b-button>
        </div>
      </div>

      <div class="table-responsive">
        <b-table
          :items="filteredLogs"
          :fields="fields"
          :busy="loading"
          hover
          responsive
          class="mb-0 email-log-table"
          :empty-text="'No email logs found.'"
          show-empty
          @row-clicked="viewLog"
          style="cursor: pointer;"
        >
          <!-- Checkbox header -->
          <template #head(selected)>
            <b-form-checkbox
              :checked="allSelected"
              :indeterminate="someSelected"
              @change="toggleSelectAll"
            />
          </template>

          <!-- Checkbox row -->
          <template #cell(selected)="{ item }">
            <b-form-checkbox
              :checked="selected.includes(item.id)"
              @change="toggleSelect(item.id)"
              @click.native.stop
            />
          </template>

          <!-- Time -->
          <template #cell(createdAt)="{ item }">
            <span class="text-nowrap" style="font-size: 13px;">
              {{ formatDate(item.createdAt) }}
            </span>
          </template>

          <!-- Receiver -->
          <template #cell(receiver)="{ item }">
            <span style="font-size: 13px;">{{ item.receiver }}</span>
          </template>

          <!-- Subject -->
          <template #cell(subject)="{ item }">
            <span style="font-size: 13px;">{{ item.subject }}</span>
          </template>

          <!-- Status -->
          <template #cell(status)="{ item }">
            <b-badge :variant="item.status === 'success' ? 'light-success' : 'light-danger'">
              <feather-icon
                :icon="item.status === 'success' ? 'CheckCircleIcon' : 'XCircleIcon'"
                size="11"
                class="mr-25"
              />
              {{ item.status === 'success' ? 'Successful' : 'Failed' }}
            </b-badge>
          </template>

          <!-- Error -->
          <template #cell(error)="{ item }">
            <span
              v-if="item.error"
              class="text-danger"
              style="font-size: 12px; max-width: 200px; display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
              :title="item.error"
            >
              {{ item.error }}
            </span>
            <span v-else class="text-muted" style="font-size: 12px;">—</span>
          </template>

          <!-- Acciones -->
          <template #cell(actions)="{ item }">
            <div class="d-flex align-items-center" style="gap: 4px;" @click.stop>
              <!-- Ver detalle -->
              <b-button
                v-b-tooltip.hover title="View details"
                variant="flat-primary"
                size="sm"
                class="btn-icon"
                @click="viewLog(item)"
              >
                <feather-icon icon="EyeIcon" size="15" />
              </b-button>
              <!-- Eliminar -->
              <b-button
                v-b-tooltip.hover title="Delete"
                variant="flat-danger"
                size="sm"
                class="btn-icon"
                @click="confirmDelete(item)"
              >
                <feather-icon icon="TrashIcon" size="15" />
              </b-button>
            </div>
          </template>

          <!-- Loading -->
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner variant="primary" label="Loading..." />
            </div>
          </template>
        </b-table>
      </div>

      <!-- Footer: total + paginación simple -->
      <div class="d-flex align-items-center justify-content-between px-1 py-75 border-top">
        <small class="text-muted">
          Showing {{ filteredLogs.length }} of {{ logs.length }} entries
        </small>
        <div class="d-flex align-items-center" style="gap: 8px;">
          <small class="text-muted">Per page:</small>
          <b-form-select
            v-model="perPage"
            :options="[25, 50, 100]"
            size="sm"
            style="width: 70px;"
          />
        </div>
      </div>
    </b-card>

    <!-- Modal: Ver detalle -->
    <b-modal
      id="modal-log-detail"
      title="Email Log Detail"
      ok-only
      ok-title="Close"
      ok-variant="outline-secondary"
      size="lg"
    >
      <div v-if="selectedLog">
        <table class="table table-sm table-bordered" style="font-size: 13px;">
          <tbody>
            <tr>
              <th style="width: 120px; background: #f8f8f8;">Time</th>
              <td>{{ formatDate(selectedLog.createdAt) }}</td>
            </tr>
            <tr>
              <th style="background: #f8f8f8;">Receiver</th>
              <td>{{ selectedLog.receiver }}</td>
            </tr>
            <tr>
              <th style="background: #f8f8f8;">Subject</th>
              <td>{{ selectedLog.subject }}</td>
            </tr>
            <tr>
              <th style="background: #f8f8f8;">Type</th>
              <td><b-badge variant="light-info">{{ selectedLog.type }}</b-badge></td>
            </tr>
            <tr>
              <th style="background: #f8f8f8;">Status</th>
              <td>
                <b-badge :variant="selectedLog.status === 'success' ? 'light-success' : 'light-danger'">
                  {{ selectedLog.status === 'success' ? 'Successful' : 'Failed' }}
                </b-badge>
              </td>
            </tr>
            <tr v-if="selectedLog.error">
              <th style="background: #f8f8f8;">Error</th>
              <td class="text-danger">{{ selectedLog.error }}</td>
            </tr>
            <tr v-if="selectedLog.uid">
              <th style="background: #f8f8f8;">User UID</th>
              <td><small class="text-muted">{{ selectedLog.uid }}</small></td>
            </tr>
            <tr v-if="selectedLog.body">
              <th style="background: #f8f8f8; vertical-align: top; padding-top: 10px;">Message</th>
              <td style="padding: 0;">
                <iframe
                  :srcdoc="selectedLog.body"
                  sandbox="allow-same-origin"
                  style="width: 100%; height: 420px; border: none; border-radius: 0 0 4px 4px;"
                  scrolling="auto"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <!-- Modal: Confirmar delete individual -->
    <b-modal
      id="modal-confirm-delete"
      title="Delete Log"
      ok-title="Delete"
      ok-variant="danger"
      cancel-variant="outline-secondary"
      @ok="executeDelete"
    >
      <p>Are you sure you want to delete this log entry?</p>
      <p v-if="logToDelete" class="text-muted" style="font-size: 13px;">
        <strong>{{ logToDelete.receiver }}</strong> — {{ formatDate(logToDelete.createdAt) }}
      </p>
    </b-modal>

    <!-- Modal: Confirmar bulk delete -->
    <b-modal
      id="modal-confirm-bulk-delete"
      title="Delete Selected Logs"
      ok-title="Delete all selected"
      ok-variant="danger"
      cancel-variant="outline-secondary"
      @ok="executeBulkDelete"
    >
      <p>You are about to delete <strong>{{ selected.length }}</strong> log entries. This action cannot be undone.</p>
    </b-modal>

  </div>

  <!-- No access -->
  <div v-else class="text-center py-5">
    <feather-icon icon="LockIcon" size="40" class="text-muted mb-1" />
    <p class="text-muted">You don't have permission to view this page.</p>
  </div>
</template>

<script>
import {
  BCard, BButton, BBadge, BTable, BFormCheckbox, BFormInput,
  BFormSelect, BSpinner, BModal, VBTooltip, VBModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapState } from 'vuex'
import axios from 'axios'
import moment from 'moment'
import { db } from '@/firebase'

const API_BASE_URL = 'https://authsys-40647711281.us-central1.run.app'

export default {
  name: 'EmailLog',
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
    'b-modal': VBModal,
  },
  components: {
    BCard, BButton, BBadge, BTable, BFormCheckbox, BFormInput,
    BFormSelect, BSpinner, BModal,
  },
  data() {
    return {
      isAdmin: false,
      loading: false,
      logs: [],
      searchQuery: '',
      activeFilter: 'all',
      selected: [],
      selectedLog: null,
      logToDelete: null,
      loadingBulkDelete: false,
      perPage: 50,

      filters: [
        { label: 'All',        value: 'all'     },
        { label: 'Successful', value: 'success' },
        { label: 'Failed',     value: 'failed'  },
      ],

      fields: [
        { key: 'selected',  label: '',          sortable: false, thStyle: 'width: 40px' },
        { key: 'createdAt', label: 'Time',      sortable: true  },
        { key: 'receiver',  label: 'Receiver',  sortable: true  },
        { key: 'subject',   label: 'Subject',   sortable: false },
        { key: 'status',    label: 'Status',    sortable: true  },
        { key: 'error',     label: 'Error',     sortable: false },
        { key: 'actions',   label: 'Actions',   sortable: false, thStyle: 'width: 90px' },
      ],
    }
  },
  computed: {
    ...mapState(['usuario']),

    filteredLogs() {
      let result = [...this.logs]

      // Filtro de status
      if (this.activeFilter !== 'all') {
        result = result.filter(l => l.status === this.activeFilter)
      }

      // Búsqueda
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        result = result.filter(l =>
          (l.receiver || '').toLowerCase().includes(q) ||
          (l.subject  || '').toLowerCase().includes(q)
        )
      }

      return result.slice(0, this.perPage)
    },

    counts() {
      return {
        all:     this.logs.length,
        success: this.logs.filter(l => l.status === 'success').length,
        failed:  this.logs.filter(l => l.status === 'failed').length,
      }
    },

    allSelected() {
      return this.filteredLogs.length > 0 &&
        this.filteredLogs.every(l => this.selected.includes(l.id))
    },

    someSelected() {
      return this.selected.length > 0 && !this.allSelected
    },
  },
  async created() {
    await this.checkAdmin()
    if (this.isAdmin) await this.loadLogs()
  },
  methods: {
    // Verificar rol admin
    async checkAdmin() {
      try {
        if (this.usuario?.role === 'admin') {
          this.isAdmin = true
          return
        }
        // Fallback: verificar en Firestore por si el store no está cargado
        const { auth } = await import('@/firebase')
        const currentUser = auth.currentUser
        if (currentUser) {
          const doc = await db.collection('usuarios').doc(currentUser.uid).get()
          if (doc.exists && doc.data().role === 'admin') {
            this.isAdmin = true
          }
        }
      } catch (e) {
        this.isAdmin = false
      }
    },

    // Cargar logs desde el backend
    async loadLogs() {
      this.loading = true
      try {
        const { auth } = await import('@/firebase')
        const token = await auth.currentUser?.getIdToken()
        const { data } = await axios.get(`${API_BASE_URL}/email-logs`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 500 },
        })
        this.logs = data.logs || []
        this.selected = []
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: 'Error', icon: 'AlertCircleIcon', variant: 'error', text: 'Could not load email logs.' },
        })
      } finally {
        this.loading = false
      }
    },

    // Filtros
    setFilter(value) {
      this.activeFilter = value
      this.selected = []
    },

    onSearch() {
      this.selected = []
    },

    // Selección
    toggleSelect(id) {
      const idx = this.selected.indexOf(id)
      if (idx === -1) this.selected.push(id)
      else this.selected.splice(idx, 1)
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selected = this.filteredLogs.map(l => l.id)
      } else {
        this.selected = []
      }
    },

    // Ver detalle
    viewLog(item) {
      this.selectedLog = item
      this.$bvModal.show('modal-log-detail')
    },

    // Delete individual
    confirmDelete(item) {
      this.logToDelete = item
      this.$bvModal.show('modal-confirm-delete')
    },

    async executeDelete() {
      if (!this.logToDelete) return
      try {
        const { auth } = await import('@/firebase')
        const token = await auth.currentUser?.getIdToken()
        await axios.delete(`${API_BASE_URL}/email-logs/${this.logToDelete.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        this.logs = this.logs.filter(l => l.id !== this.logToDelete.id)
        this.selected = this.selected.filter(id => id !== this.logToDelete.id)
        this.logToDelete = null
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: 'Deleted', icon: 'TrashIcon', variant: 'success', text: 'Log entry deleted.' },
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: 'Error', icon: 'AlertCircleIcon', variant: 'error', text: 'Could not delete log.' },
        })
      }
    },

    // Bulk delete
    confirmBulkDelete() {
      if (this.selected.length === 0) return
      this.$bvModal.show('modal-confirm-bulk-delete')
    },

    async executeBulkDelete() {
      this.loadingBulkDelete = true
      try {
        const { auth } = await import('@/firebase')
        const token = await auth.currentUser?.getIdToken()
        await axios.delete(`${API_BASE_URL}/email-logs`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { ids: this.selected },
        })
        const deleted = new Set(this.selected)
        this.logs = this.logs.filter(l => !deleted.has(l.id))
        const count = this.selected.length
        this.selected = []
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: 'Deleted', icon: 'TrashIcon', variant: 'success', text: `${count} log entries deleted.` },
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: 'Error', icon: 'AlertCircleIcon', variant: 'error', text: 'Could not delete logs.' },
        })
      } finally {
        this.loadingBulkDelete = false
      }
    },

    // Helpers
    formatDate(iso) {
      if (!iso) return '—'
      return moment(iso).format('MMM D, YYYY h:mm a')
    },
  },
}
</script>

<style scoped>
.email-log-table ::v-deep thead th {
  background-color: #f8f8f8;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  border-bottom: 2px solid #ebe9f1;
  padding: 10px 12px;
}
.email-log-table ::v-deep tbody td {
  padding: 10px 12px;
  vertical-align: middle;
  border-color: #f0f0f0;
}
.email-log-table ::v-deep tbody tr:hover {
  background-color: #fdf5f5;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
</style>